import { useState, useEffect } from 'react'
import Button from './Button'
import { audioEffects } from '../utils/audioEffects'
import { unlockEasterEgg } from '../utils/easterEggUnlocks'
import { getEasterEggConfig } from '../config/easterEggs'
import './TypeWriterManager.css'

interface TypeWriterManagerProps {
  isOpen: boolean
  onClose: () => void
  defaultTexts: string[]
  onTextsChange: (texts: string[]) => void
  onEasterEggUnlock?: (easterEggName: string) => void
}

function TypeWriterManager({ isOpen, onClose, defaultTexts, onTextsChange, onEasterEggUnlock }: TypeWriterManagerProps) {
  const [texts, setTexts] = useState<string[]>([])
  const [newText, setNewText] = useState('')
  const [showNotification, setShowNotification] = useState('')
  const [notificationType, setNotificationType] = useState<'success' | 'error'>('success')
  const [isResetSuccess, setIsResetSuccess] = useState(false)

  useEffect(() => {
    if (isOpen) {
      // Load current texts from localStorage or use default
      const savedTexts = localStorage.getItem('typewriter-texts')
      if (savedTexts) {
        try {
          setTexts(JSON.parse(savedTexts))
        } catch (error) {
          console.warn('Failed to parse saved texts:', error)
          setTexts(defaultTexts)
        }
      } else {
        setTexts(defaultTexts)
      }
    }
  }, [isOpen, defaultTexts])

  const showNotificationMessage = (message: string, type: 'success' | 'error' = 'success') => {
    setShowNotification(message)
    setNotificationType(type)
    setTimeout(() => setShowNotification(''), 2000)
  }

  const handleAddText = () => {
    if (newText.trim() && !texts.includes(newText.trim())) {
      const updatedTexts = [...texts, newText.trim()]
      setTexts(updatedTexts)
      setNewText('')
      showNotificationMessage('✓ Tekst toegevoegd', 'success')
    } else if (texts.includes(newText.trim())) {
      showNotificationMessage('⚠ Deze tekst bestaat al', 'error')
    }
  }

  const handleDeleteText = (index: number) => {
    if (texts[index] === 'Luuk Verboeket') {
      showNotificationMessage("⚠ Zo heet ik gewoon...", 'error')
      return
    }
    if (texts.length > 1) {
      const updatedTexts = texts.filter((_, i) => i !== index)
      setTexts(updatedTexts)
      showNotificationMessage('✓ Tekst verwijderd', 'success')
    } else {
      showNotificationMessage('⚠ Minimaal één tekst vereist', 'error')
    }
  }

  const handleResetToDefault = () => {
    // Play success sound immediately
    audioEffects.playSuccessChime()
    showNotificationMessage('✓ Teksten teruggezet', 'success')
    
    setTexts(defaultTexts)
    
    // Show success state immediately
    setIsResetSuccess(true)
    setTimeout(() => {
      setIsResetSuccess(false)
    }, 1000)
  }

  const handleSave = () => {
    // Save to localStorage
    try {
      localStorage.setItem('typewriter-texts', JSON.stringify(texts))
      onTextsChange(texts)
      
      // Check if user has added any custom messages (not in defaultTexts)
      const hasCustomMessages = texts.some(text => !defaultTexts.includes(text))
      let easterEggUnlocked = false
      
      if (hasCustomMessages) {
        const customMessageEasterEgg = getEasterEggConfig('custom_message')
        const wasNewlyUnlocked = unlockEasterEgg(customMessageEasterEgg?.id || 'custom_message')
        if (wasNewlyUnlocked) {
          easterEggUnlocked = true
        }
      }
      
      showNotificationMessage('✓ Wijzigingen opgeslagen', 'success')
      
      // Close modal after a brief delay to show the notification
      setTimeout(() => {
        onClose()
        
        // Show easter egg notification after modal is closed
        if (easterEggUnlocked && onEasterEggUnlock) {
          setTimeout(() => {
            onEasterEggUnlock('Lief <3 (Hopelijk)')
          }, 200) // Small delay to ensure modal is fully closed
        }
      }, 1000)
    } catch (error) {
      console.warn('Failed to save texts:', error)
      showNotificationMessage('⚠ Fout bij opslaan', 'error')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddText()
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="typewriter-manager-overlay" onClick={onClose}>
      <div className="typewriter-manager-modal" onClick={(e) => e.stopPropagation()}>
        <div className="typewriter-manager-header">
          <h3>Wat ben ik volgens jou?</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="typewriter-manager-content">
          <div className="current-texts">
            <h4>Wat ik ben:</h4>
            <div className="texts-list">
              {texts.map((text, index) => (
                <div key={index} className="text-item">
                  <span className="text-content">{text}</span>
                  <button 
                    className="delete-button"
                    onClick={() => handleDeleteText(index)}
                    title="Verwijder tekst"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="add-text-section">
            <h4>Noem me wat je wilt</h4>
            <div className="add-text-input-container">
              <input
                type="text"
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                onKeyDown={handleKeyDown}
                className="add-text-input"
                placeholder="Nieuwe tekst..."
                maxLength={25}
              />
              <Button 
                variant="primary"
                onClick={handleAddText}
                text="Toevoegen"
                disabled={!newText.trim()}
                className="add-text-button"
              />
            </div>
          </div>
        </div>

        <div className="typewriter-manager-footer">
          <Button 
            variant="reset"
            onClick={handleResetToDefault}
            text={isResetSuccess ? "✓" : "↻"}
            className={isResetSuccess ? 'success' : ''}
            disabled={isResetSuccess}
          />
          <div className="footer-buttons-right">
            <Button 
              variant="cancel"
              onClick={onClose}
              text="Annuleren"
            />
            <Button 
              variant="confirm"
              onClick={handleSave}
              text="Opslaan"
            />
          </div>
        </div>

        {showNotification && (
          <div className={`typewriter-notification typewriter-notification-${notificationType}`}>
            {showNotification}
          </div>
        )}
      </div>
    </div>
  )
}

export default TypeWriterManager
